import { provideHttpClient, withInterceptorsFromDi, withFetch } from '@angular/common/http';
import { ApplicationConfig, LOCALE_ID } from '@angular/core';
import { provideRouter, RouteReuseStrategy, withComponentInputBinding, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore, Store } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { CookieConfig, InitCookieSettingsSuccess, ProvideCookiesNgrx } from '@teamfoster/sdk/cookie-ngrx';
import { provideCmsPreview } from '@teamfoster/live-preview/cms-preview';
import { ProvideSignalsDictionary } from '@teamfoster/sdk/dictionary-signals';
import { ProvideSignalsMenu } from '@teamfoster/sdk/menu-signals';
import { ProvideIcons } from '@teamfoster/sdk/icon';
import { ImageSharpConfig, ProvideOptimisedImageProcessor } from '@teamfoster/sdk/image';
import { environment } from 'src/environments/environment';
import { routes } from './app.routes';
import { reducers, metaReducers, appEffects, CustomSerializer } from './store';
import { ProvideSocialsNgrx } from './social-media/socials.providers';
import { provideClientHydration, withIncrementalHydration } from '@angular/platform-browser';
import { AppRouteReuseStrategy } from './app.route-reuse-strategy';
import { registerLocaleData } from '@angular/common';

import localeNl from '@angular/common/locales/nl';
import { CMS_ORIGINS } from 'src/cms-origins';

const cookieConfig: CookieConfig = {
  analyticsCode: 'G-SCQ13XWW5L',
  strippedContentPlaceholder: 'Deze inhoud wordt niet getoond omdat je geen marketing cookies hebt geaccepteerd',
  buttonSettings: 'Instellingen',
  buttonAcceptAll: 'Alle accepteren',
  buttonClose: 'Sluiten',
  strippedContentButton: 'Cookie instellingen wijzigen',
  types: {
    analytical: true,
    functional: true,
    marketing: true,
  },
  showSettings: true,
};

registerLocaleData(localeNl, 'nl');

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({
        paramsInheritanceStrategy: 'emptyOnly',
      }),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'disabled' })
    ),
    provideStore(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
      },
    }),
    provideEffects(appEffects),
    ProvideSocialsNgrx({}),
    provideRouterStore({ serializer: CustomSerializer }),
    provideStoreDevtools({ maxAge: 25, logOnly: environment.production }),
    ProvideSignalsDictionary({}),
    ProvideSignalsMenu({}),
    ProvideIcons({ spritesheetUrl: 'assets/dist/svg/symbol/svg/sprite.symbol.svg' }),
    ProvideCookiesNgrx(cookieConfig),
    ProvideOptimisedImageProcessor(ImageSharpConfig, {
      placeholderUrl: 'https://www.teamfoster.nl/media/uploads/Placeholder_view_vector.svg',
      defaultFormat: 'webp',
    }),
    provideClientHydration(withIncrementalHydration()),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideCmsPreview({
      allowedOrigins: CMS_ORIGINS,
      loadContentTypes: () => import('./cms-preview/cms-preview.config').then(m => m.contentTypes),
      // Inside the CMS preview: no cookie notice and no analytics, without reading or writing the stored preferences
      onActivate: injector =>
        injector.get(Store).dispatch(InitCookieSettingsSuccess({ preferences: { functional: true, analytical: false, marketing: true } })),
    }),
    { provide: LOCALE_ID, useValue: 'nl' },
    //{ provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
  ],
};
